import * as React from 'react';
import { NextPage } from 'next';
import { NextSeo } from 'next-seo';
import { Link, PageTitle } from '@/components/common';

const PrivacyPage: NextPage = () => {
  return <>
      <NextSeo title='Privacy Policy' />
      <PageTitle>Privacy Policy</PageTitle>
      <div className='space-y-4 mx-auto px-6 max-w-4xl md:px-12'>
        <p>Last modified: 5/8/2023</p>
        <h3 className='font-bold text-lg'>Introduction</h3>
        <p>
          <strong>28 Wellness, Inc.</strong> (the “Company” or “We”) respect your privacy and are committed to
          protecting it through our compliance with this policy.
        </p>
        <p>
          This policy describes the types of information we may collect from you or that you may provide when you visit
          the website <strong>www.28.co</strong> (our “Website”) or use our mobile application, 28 (the “App”) and our
          practices for collecting, using, maintaining, protecting, and disclosing that information. This policy applies
          to information we collect:
        </p>
        <ul className='list-disc ml-4 [&>li]:pl-3'>
          <li>On this Website or through the App.</li>
          <li>In email, text, and other electronic messages between you and this Website or the App.</li>
          <li>
            When you interact with our advertising and applications on third-party websites and services if those
            applications or advertising include links to this policy.
          </li>
        </ul>
        <p>It does not apply to information collected by:</p>
        <ul className='list-disc ml-4 [&>li]:pl-3'>
          <li>
            us offline or through any other means, including on any other website operated by Company or any third party
            (including our affiliates and subsidiaries); or
          </li>
          <li>
            any third party (including our affiliates), including through any application or content (including
            advertising) that may link to or be accessible from or on the Website or the App.
          </li>
        </ul>
        <p>
          Please read this policy carefully to understand our policies and practices regarding your information and how
          we will treat it. If you do not agree with our policies and practices, your choice is not to use our Website
          or the App. By accessing or using this Website or the App, you agree to this privacy policy. This policy may
          change from time to time (see Changes to Our Privacy Policy). Your continued use of this Website or the App
          after we make changes is deemed to be acceptance of those changes, so please check the policy periodically for
          updates.
        </p>
        <h3 className='font-bold text-lg'>Children Under the Age of 13</h3>
        <p>
          Our Website and the App is not intended for children under 13 years of age. No one under age 13 may provide
          any personal information to or on the Website or App. We do not knowingly collect personal information from
          children under 13. If you are under 13, do not use or provide any information on this Website or the App or on
          or through any of its features on the Website or App, make any purchases through the Website or App, use any
          of the interactive or public comment features of this Website or App or provide any information about yourself
          to us, including your name, address, telephone number, email address, or any screen name or user name you may
          use. If we learn we have collected or received personal information from a child under 13 without verification
          of parental consent, we will delete that information. If you believe we might have any information from or
          about a child under 13, please contact us at{' '}
          <a className='underline' href='mailto:support@28.co'>
            support@28.co
          </a>
          .
        </p>
        <p>
          California residents under 16 years of age may have additional rights regarding the collection and sale of
          their personal information. Please see Your California Privacy Rights for more information.
        </p>
        <h3 className='font-bold text-lg'>Information We Collect About You and How We Collect It</h3>
        <p>
          We collect several types of information from and about users of our Website or App, including information by
          which you may be personally identified, such as name, postal address, e-mail address, telephone number, or any
          other identifier by which you may be contacted online or offline (“personal information”); that is about you
          but individually does not identify you, such as your internet connection, the equipment you use to access our
          Website or App and usage details.
        </p>
        <p>We collect this information:</p>
        <ul className='list-disc ml-4 [&>li]:pl-3'>
          <li>Directly from you when you provide it to us.</li>
          <li>
            Automatically as you navigate through the site. Information collected automatically may include usage
            details, IP addresses, and information collected through cookies, web beacons, and other tracking
            technologies.
          </li>
        </ul>
        <h3 className='font-bold text-lg italic'>Information You Provide To Us</h3>
        <p>The information we collect on or through our Website or App may include:</p>
        <ul className='list-disc ml-4 [&>li]:pl-3'>
          <li>
            Information that you provide by filling in forms on our Website or App. This includes information provided
            at the time of registering to use our Website or App, subscribing to our services, emails, or website,
            submitting content, posting material, or requesting further services. We may also ask you for information
            when you report a problem with our Website or App.
          </li>
          <li>Records and copies of your correspondence (including email addresses) if you contact us.</li>
          <li>Your responses to surveys that we might ask you to complete for research purposes</li>
          <li>
            Details of transactions you carry out through our Website or App and of the fulfillment of your orders. You
            may be required to provide financial information before placing an order through our Website or App.
          </li>
          <li>Your search queries on the Website or App.</li>
        </ul>
        <h3 className='font-bold text-lg italic'>
          Information We Collect Through Automatic Data Collection Technologies
        </h3>
        <p>
          As you navigate through and interact with our Website or App, we may use automatic data collection
          technologies to collect certain information about your equipment, browsing actions, and patterns, including:
        </p>
        <ul className='list-disc ml-4 [&>li]:pl-3'>
          <li>
            Details of your visits to our Website or App, including traffic data, location data, logs, and other
            communication data and the resources that you access and use on the Website or App.
          </li>
          <li>
            Information about your computer and internet connection, including your IP address, operating system, and
            browser type.
          </li>
          <li>
            We also may use these technologies to collect information about your online activities over time and across
            third-party websites or other online services (behavioral tracking).
          </li>
        </ul>
        <p>
          The information we collect automatically is statistical data and may include personal information, or we may
          maintain it or associate it with personal information we collect in other ways. It helps us to improve our
          Website and App and to deliver a better and more personalized service, including by enabling us to:
        </p>
        <ul className='list-disc ml-4 [&>li]:pl-3'>
          <li>Estimate our audience size and usage patterns.</li>
          <li>
            Store information about your preferences, allowing us to customize our Website and App according to your
            individual interests.
          </li>
          <li>Speed up your searches.</li>
          <li>Recognize you when you return to our Website or App.</li>
        </ul>
        <p>The technologies we use for this automatic data collection may include:</p>
        <ul className='list-disc ml-4 [&>li]:pl-3'>
          <li>
            <strong>Cookies (or browser cookies)</strong>. A cookie is a small file placed on the hard drive of your
            computer. You may refuse to accept browser cookies by activating the appropriate setting on your browser.
            However, if you select this setting, you may be unable to access certain parts of our Website or App. Unless
            you have adjusted your browser setting so that it will refuse cookies, our system will issue cookies when
            you direct your browser to our Website or App.
          </li>
          <li>
            <strong>Flash Cookies.</strong> Certain features of our Website or App may use local stored objects (or
            Flash cookies) to collect and store information about your preferences and navigation to, from, and on our
            Website or App. Flash cookies are not managed by the same browser settings as are used for browser cookies.
          </li>
          <li>
            <strong>Web Beacons.</strong>. Pages of the Website or App and our e-mails may contain small electronic
            files known as web beacons (also referred to as clear gifs, pixel tags, and single-pixel gifs) that permit
            the Company, for example, to count users who have visited those pages or opened an email and for other
            related website statistics (for example, recording the popularity of certain website content and verifying
            system and server integrity).
          </li>
        </ul>
        <h3 className='font-bold text-lg'>Third-Party Use of Cookies and Other Tracking Technologies</h3>
        <p>
          Some content or applications on the Website or App are served by third parties, including servers, content
          providers, and application providers. These third parties may use cookies alone or in conjunction with web
          beacons or other tracking technologies to collect information about you when you use our website. The
          information they collect may be associated with your personal information or they may collect information,
          including personal information, about your online activities over time and across different websites and other
          online services.
        </p>
        <p>
          We do not control these third parties&apos; tracking technologies or how they may be used. If you have any
          questions about any targeted content, you should contact the responsible provider directly.
        </p>
        <h3 className='font-bold text-lg'>How We Use Your Information</h3>
        <p>
          We use information that we collect about you or that you provide to us, including any personal information:
        </p>
        <ul className='list-disc ml-4 [&>li]:pl-3'>
          <li>To present our Website and App and its contents to you.</li>
          <li>To provide you with information, products, or services that you request from us.</li>
          <li>To fulfill any other purpose for which you provide it.</li>
          <li>To provide you with notices about your subscription, including expiration and renewal notices.</li>
          <li>
            To carry out our obligations and enforce our rights arising from any contracts entered into between you and
            us, including for billing and collection.
          </li>
          <li>
            To notify you about changes to our Website and App or any products or services we offer or provide though
            it.
          </li>
          <li>To allow you to participate in interactive features on our Website and App.</li>
          <li>For any other purpose with your consent.</li>
        </ul>
        <h3 className='font-bold text-lg'>Disclosure of Your Information</h3>
        <p>
          We will never sell your personal information. We will never disclose your personal information other than on a
          limited basis as described in this Privacy Policy.
        </p>
        <p>
          We may disclose aggregated information about our users and information that does not identify any individual,
          without restriction.
        </p>
        <p>We may disclose personal information that we collect or you provide as described in this privacy policy:</p>
        <ul className='list-disc ml-4 [&>li]:pl-3'>
          <li>To our parent company and subsidiaries.</li>
          <li>
            To contractors, service providers, and other third parties we use to support our business who are subject to
            an obligation of confidentiality.
          </li>
          <li>
            To a buyer or other successor in the event of a merger, divestiture, restructuring, reorganization,
            dissolution, or other sale or transfer of some or all of 28 Wellness, Inc.&apos;s assets, whether as a going
            concern or as part of bankruptcy, liquidation, or similar proceeding, in which personal information held by
            28 Wellness, Inc. about our Website and App users is among the assets transferred.
          </li>
          <li>To fulfill the purpose for which you provide it.</li>
          <li>For any other purpose disclosed by us when you provide the information.</li>
        </ul>
        <p>We may also disclose your personal information:</p>
        <ul className='list-disc ml-4 [&>li]:pl-3'>
          <li>
            To comply with any court order, law, or legal process, including as required to respond to any government or
            regulatory request, but only to the extent legally required. We will never voluntarily provide your personal
            information to the government.
          </li>
          <li>
            To enforce or apply our <Link href='/terms'>terms of use</Link> and other agreements, including for billing
            and collection purposes.
          </li>
          <li>
            If we believe disclosure is necessary or appropriate to protect the rights, property, or safety of 28
            Wellness, Inc., our customers, or others.
          </li>
        </ul>
        <p>However, with respect to any data you provide regarding monthly menstrual cycle tracking:</p>
        <ul className='list-disc ml-4 [&>li]:pl-3'>
          <li>The only cycle data we collect is data you choose to provide on a voluntary basis.</li>
          <li>Any cycle data you provide may be changed or deleted by you at any time.</li>
          <li>
            We do not store deleted cycle data. If you change or delete the cycle data you provided, it is gone forever.
          </li>
          <li>We will never disclose your cycle data to the government or any legal authority.</li>
        </ul>
        <h3 className='font-bold text-lg'>Choices About How We Use and Disclose Your Information</h3>
        <p>
          We strive to provide you with choices regarding the personal information you provide to us. We have created
          mechanisms to provide you with the following control over your information:
        </p>
        <p>
          <strong>Tracking Technologies and Advertising.</strong> You can set your browser to refuse all or some browser
          cookies, or to alert you when cookies are being sent. To learn how you can manage your Flash cookie settings,
          visit the Flash player settings page on Adobe&apos;s website. If you disable or refuse cookies, please note
          that some parts of this site may then be inaccessible or not function properly.
        </p>
        <p>
          <strong>Disclosure of Your Information for Third-Party Advertising.</strong> We do not share personal
          information with unaffiliated or non-agent third parties for promotional purposes.
        </p>
        <p>
          <strong>Promotional Offers from the Company.</strong> If you do not wish to have your contact information used
          by the Company to promote our own or third parties&apos; products or services, you can opt-out by sending us
          an email stating your request to{' '}
          <a className='underline' href='mailto:support@28.co'>
            support@28.co
          </a>
          .You can also unsubscribe at any time. This opt-out does not apply to information provided to the Company as a
          result of a product purchase, warranty registration, product service experience or other transactions.
        </p>
        <p>
          <strong>Targeted Advertising.</strong> We do not use information that we collect or that you provide to us to
          deliver advertisements to you.
        </p>
        <p>
          Residents of certain states, such as California, Nevada, Colorado, Connecticut, Virginia, and Utah, may have
          additional personal information rights and choices. Please see “Your State Privacy Rights” for more
          information.
        </p>
        <h3 className='font-bold text-lg underline'>Accessing, Correcting, and Deleting Your Information</h3>
        <p>
          You may also send us an email at{' '}
          <a className='underline' href='mailto:support@28.co'>
            support@28.co
          </a>{' '}
          to request access to, correct or delete any personal information that you have provided to us. We may not
          accommodate a request to change information if we believe the change would violate any law or legal
          requirement or cause the information to be incorrect.
        </p>
        <h3 className='font-bold text-lg underline'>Your State Privacy Rights</h3>
        <p>
          Residents of certain states, such as California, Nevada, Colorado, Virginia, and Utah may have additional
          personal information rights and choices.
        </p>
        <p>Your California Privacy Rights</p>
        <p>
          If you are a California resident, California law may provide you with additional rights regarding our use of
          your personal information. For example, California&apos;s “Shine the Light” law (Civil Code Section § 1798.83)
          permits users of the Website and App that are California residents to request certain information regarding
          our disclosure of personal information to third parties for their direct marketing purposes. To make such a
          request, please send an email to <a href='mailto:support@28.co'>support@28.co</a>.
        </p>
        <h3 className='font-bold text-lg'>Consumer Privacy Rights in Other States</h3>
        <p>Colorado, Connecticut, Virginia, and Utah each provide their state residents with rights to:</p>
        <ul className='list-disc ml-4 [&>li]:pl-3'>
          <li>Confirm whether we process their personal information.</li>
          <li>Access and delete certain personal information.</li>
          <li>Data portability.</li>
          <li>Opt-out of personal data processing for targeted advertising and sales.</li>
        </ul>
        <p>Colorado, Connecticut, and Virginia also provide their state residents with rights to:</p>
        <ul className='list-disc ml-4 [&>li]:pl-3'>
          <li>
            Correct inaccuracies in their personal information, taking into account the information&apos;s nature
            processing purpose.
          </li>
          <li>Opt-out of profiling in furtherance of decisions that produce legal or similarly significant effects.</li>
        </ul>
        <p>
          To exercise any of these rights please send an email to{' '}
          <a className='underline' href='mailto:support@28.co'>
            support@28.co
          </a>
          . To appeal a decision regarding a consumer rights request send an email to{' '}
          <a className='underline' href='mailto:support@28.co'>
            support@28.co
          </a>
          .
        </p>
        <p>
          Nevada provides its residents with a limited right to opt-out of certain personal information sales. Residents
          who wish to exercise this sale opt-out rights may submit a request to this designated address:{' '}
          <a className='underline' href='mailto:support@28.co'>
            support@28.co
          </a>
          . However, please know we do not currently sell data triggering that statute&apos;s opt-out requirements.
        </p>
        <h3 className='font-bold text-lg'>Data Security</h3>
        <p>
          We have implemented measures designed to secure your personal information from accidental loss and from
          unauthorized access, use, alteration, and disclosure. We urge you to be careful about giving out information
          in public areas of the Website and App like the comment sections of articles. The information you share in
          public areas may be viewed by any user of the Website or App.
        </p>
        <p>
          Unfortunately, the transmission of information via the internet is not completely secure. Although we do our
          best to protect your personal information, we cannot guarantee the security of your personal information
          transmitted to our Website or App. Any transmission of personal information is at your own risk. We are not
          responsible for circumvention of any privacy settings or security measures contained on the Website or App
        </p>
        <h3 className='font-bold text-lg'>Changes to Our Privacy Policy</h3>
        <p>
          It is our policy to post any changes we make to our privacy policy on this page. The date the privacy policy
          was last revised is identified at the top of the page. You are responsible for periodically visiting our
          Website and this privacy policy to check for any changes.
        </p>
        <h3>Contact Information</h3>
        <p>To ask questions or comment about this privacy policy and our privacy practices, contact us at:</p>
        <p>
          <a className='underline' href='mailto:support@28.co'>
            support@28.co
          </a>
          .
        </p>
      </div>
    </>;
};

export default PrivacyPage;